// Brief 85 §2 — ContactPersonModal, moved out of App.tsx's renderModals switch.
//
// MOVED, NOT REWRITTEN. The body below is byte-identical to the `case "cp":`
// clause it left —
//   sha256 af1850d11bfc49245a30ec416f152012b3b5f843bdb335864add52e5f02f653e
// — including its original indentation, which is why it sits deeper than this
// file's own wrapper. Only the imports, the Props type, the signature and the
// `return (` / `);` are new.
// Do not reformat: the indentation IS the proof.
//
// Props are the UNION of the names the body actually read in App scope, taken
// from a TypeScript-checker walk rather than chosen — a passed-but-unread prop
// would mean the union rule absorbed something it should have refused.
// The vocabulary props are typed by inference off makeModalFields' return, so
// this file adds nothing to the `any` ratchet.

/* 189 §E — `import React`: react/react-in-jsx-scope is counted per JSX
   element and this is the rule's own remedy (168A's method). */
import React from "react";
import { ModalLayout } from "../SharedUI";
import { SK, pname } from "../../utils";
import type { TFn, TaxList } from "../../lib/taxonomyTypes";
import type { makeModalFields } from "../modalFields";
// 189 §E — the full form: the client link, the address, the notes the model
// already carries, and the CDD block behind a disclosure.
import { useState } from "react";
import FieldSelect from "../FieldSelect";
import type { FieldSelectOption } from "../FieldSelect";
import MenuSelect from "../MenuSelect";
import { isClientCounsel } from "../../lib/counselLinks"; // 214 §4 — the role Avocat

type Vocab = ReturnType<typeof makeModalFields>;
type ModalForm = { bar?: string | null; clientId?: number | string | null; country?: string | null; dateOfBirth?: string | null; id?: string | null; idDocumentExpiresAt?: string | null; idDocumentIssuedAt?: string | null; idDocumentNumber?: string | null; idDocumentType?: string | null; nationality?: string | null; placeOfBirth?: string | null; role?: string | null };
/* 189 §E — what the client picker reads off a client row. */
interface ClientLike { id?: number | null; email?: string | null }

type Props = {
  F: Vocab["F"];
  NA: Vocab["NA"];
  T: Vocab["T"];
  TitSel: Vocab["TitSel"];
  _fl: Vocab["_fl"];
  _mt: Vocab["_mt"];
  /* 214 §4 [RULED 53] — the role list and, for the role Avocat, the bar list (the same kind an opposing counsel picks from). */
  barEntries: TaxList;
  barsList: readonly FieldSelectOption[];
  clients: readonly unknown[];
  cm: () => void;
  contactPersons: readonly unknown[];
  contactRoleEntries: TaxList;
  contactRolesList: readonly FieldSelectOption[];
  countriesList: readonly FieldSelectOption[];
  countryEntries: TaxList;
  form: ModalForm;
  saveEntity: (k: string, rows: readonly unknown[], set: (v: readonly unknown[]) => void, f: unknown, ext?: Record<string, unknown>) => void;
  setContactPersons: (v: readonly unknown[]) => void;
  t: TFn;
  updateField: (k: string, v: unknown) => void;
};

export default function ContactPersonModal({
  F,
  NA,
  T,
  TitSel,
  _fl,
  _mt,
  barEntries,
  barsList,
  clients,
  cm,
  contactPersons,
  contactRoleEntries,
  contactRolesList,
  countriesList,
  countryEntries,
  form,
  saveEntity,
  setContactPersons,
  t,
  updateField,
}: Props) {
  /* 189 §E — THIS IS THE FULL FORM (the Persons → Contact Persons list's add
     and edit). MEASURED: ContactPerson carries 27 fields and two forms mount
     one — this modal and the case's quick-add (QuickAddModal, type "cp") —
     and until 189 both showed the whole CDD block while neither showed the
     address, the notes, or (here) the client link the model holds. Now: the
     compact fields first — identity, the client, contact details, address,
     notes — and the identity-document block (date and place of birth,
     nationality, the four ID-document fields) behind a disclosure, open when
     any of them already holds a value. Nothing is added to the model. */
  const cddValues = [form.dateOfBirth, form.placeOfBirth, form.nationality, form.idDocumentType, form.idDocumentNumber, form.idDocumentIssuedAt, form.idDocumentExpiresAt];
  const [cddOpen, setCddOpen] = useState(cddValues.some((v) => v != null && v !== ""));
  const clientOptions: readonly (readonly [string, string])[] = (clients as readonly ClientLike[])
    .filter((c) => c.id != null)
    .map((c) => [String(c.id), pname(c)] as const);
  return (
          <ModalLayout
            recordId={form.id} // 204 §G — the person's internal id in the header
            title={form.id ? _mt("Edit Contact") : _mt("New Contact")}
            onClose={cm}
            onSave={() => saveEntity(SK.cp, contactPersons, setContactPersons, form)}
          >
              <>
                <div className="field-row">
                  {TitSel}
                  {F("First Name *", "firstName", "", 1)}
                </div>
                <div className="field-row">
                  {F("Last Name *", "lastName")}
                  {F("Function / Role", "function")}
                </div>
                {/* 214 §4 [RULED 53] — the role, from the contact_role list (a FieldSelect, never a free-text input on a
                    converted column — scripts/lint/taxonomy-free-text-writers.js). A contact with the role Avocat is the
                    client's own counsel: it also asks « Barreau », and its company reads « Cabinet » below. */}
                <div className="field-row" data-contact-role-row="">
                  <FieldSelect
                    label={String(_fl("Role"))}
                    value={form.role}
                    onChange={(v) => { updateField("role", v || null); }}
                    options={[["", "—"], ...contactRolesList]}
                    entries={contactRoleEntries}
                    t={t}
                  />
                  {isClientCounsel(form) ? (
                    <FieldSelect
                      label={String(_fl("Bar"))}
                      value={form.bar}
                      onChange={(v) => { updateField("bar", v || null); }}
                      options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...barsList]}
                      entries={barEntries}
                      t={t}
                    />
                  ) : null}
                </div>
                {NA({
                  pool: contactPersons,
                  typeLabel: t("type.contact_person", {
                    fallback: "contact person",
                  }),
                })}
                <div className="field">
                  <label>{_fl("Linked Client")}</label>
                  <MenuSelect
                    className="field-input"
                    value={form.clientId != null && form.clientId !== "" ? String(form.clientId) : ""}
                    onChange={(v) => { updateField("clientId", v ? Number(v) : null); }}
                    options={[["", t("common.none_dashed", { fallback: "— None —" })] as const, ...clientOptions]}
                  />
                </div>
                <div className="field-row">
                  {F("Email", "email")}
                  {F("Phone", "phone")}
                </div>
                {isClientCounsel(form) ? F("Firm", "company") : F("Company", "company")}
                {F("Street", "street")}
                <div className="field-row">
                  {F("Postal Code", "postalCode")}
                  {F("City", "city")}
                </div>
                <FieldSelect
                  label={t("field.country", { fallback: "Country" })}
                  value={form.country}
                  onChange={(v) => { updateField("country", v || null); }}
                  options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...countriesList]}
                  searchable
                  entries={countryEntries}
                  t={t}
                />
                {T("Notes", "notes")}
                <button
                  type="button"
                  className="btn btn-ghost"
                  aria-expanded={cddOpen}
                  style={{ alignSelf: "flex-start", margin: "6px 0 2px" }}
                  onClick={() => { setCddOpen((o) => !o); }}
                >
                  {cddOpen ? "▾ " : "▸ "}
                  {t("cdd.section.identity_block", { fallback: "Identity documents (CDD)" })}
                </button>
                {cddOpen && (<>
                <div className="field-row">
                  <div className="field">
                    <label>
                      {_fl(t("cdd.field.dob", { fallback: "Date of birth" }))}
                    </label>
                    <input
                      className="field-input"
                      type="date"
                      value={
                        form.dateOfBirth ? String(form.dateOfBirth).slice(0, 10) : ""
                      }
                      onChange={(e) =>
                        updateField("dateOfBirth", e.target.value || null)
                      }
                    />
                  </div>
                  {F(
                    t("cdd.field.pob", { fallback: "Place of birth" }),
                    "placeOfBirth",
                  )}
                </div>
                {F(
                  t("cdd.field.nationality", { fallback: "Nationality" }),
                  "nationality",
                )}
                <div className="field-row">
                  {F(
                    t("cdd.field.id_doc_type", {
                      fallback: "ID document type",
                    }),
                    "idDocumentType",
                    t("cdd.field.id_doc_type_ph", {
                      fallback: "passport / national_id",
                    }),
                  )}
                  {F(
                    t("cdd.field.id_doc_number", {
                      fallback: "ID document number",
                    }),
                    "idDocumentNumber",
                  )}
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>
                      {_fl(
                        t("cdd.field.id_doc_issued_at", {
                          fallback: "ID issued",
                        }),
                      )}
                    </label>
                    <input
                      className="field-input"
                      type="date"
                      value={
                        form.idDocumentIssuedAt
                          ? String(form.idDocumentIssuedAt).slice(0, 10)
                          : ""
                      }
                      onChange={(e) =>
                        updateField("idDocumentIssuedAt", e.target.value || null)
                      }
                    />
                  </div>
                  <div className="field">
                    <label>
                      {_fl(
                        t("cdd.field.id_doc_expires_at", {
                          fallback: "ID expires",
                        }),
                      )}
                    </label>
                    <input
                      className="field-input"
                      type="date"
                      value={
                        form.idDocumentExpiresAt
                          ? String(form.idDocumentExpiresAt).slice(0, 10)
                          : ""
                      }
                      onChange={(e) =>
                        updateField("idDocumentExpiresAt", e.target.value || null)
                      }
                    />
                  </div>
                </div>
                </>)}
              </>
          </ModalLayout>
  );
}
